<template>
  <el-dialog
    v-model="dialogVisible"
    append-to-body
    class="new-lark-dialog"
    :close-on-click-modal="false"
    destroy-on-close
    :show-close="false"
    :width="600"
  >
    <template #header>
      <div class="new-lark-dialog__header">
        <div class="new-lark-dialog__title">{{ title }}</div>
        <button class="new-lark-dialog__close" type="button" @click="dialogVisible = false">
          <i class="icon-close-large-line t-iconfont"></i>
        </button>
      </div>
    </template>

    <div class="new-lark-dialog__body">
      <div class="new-lark-dialog__field">
        <div class="new-lark-dialog__label">群聊名称</div>
        <el-input
          v-model="formData.groupName"
          class="new-lark-dialog__input-text"
          maxlength="60"
          show-word-limit
        />
      </div>

      <div class="new-lark-dialog__field new-lark-dialog__field--members">
        <div class="new-lark-dialog__label">群成员列表</div>
        <div class="new-lark-dialog__member-panel">
          <div class="new-lark-dialog__member-item">
            <div class="new-lark-dialog__avatar new-lark-dialog__avatar--robot">
              <i class="icon-robot-2-fill t-iconfont"></i>
            </div>
            <div class="new-lark-dialog__member-name">飞书机器人（机器人）</div>
          </div>
          <div
            v-for="item in displayMemberList"
            :key="item.id"
            class="new-lark-dialog__member-item"
          >
            <div class="new-lark-dialog__avatar new-lark-dialog__avatar--text">
              {{ item.avatarText }}
            </div>
            <div class="new-lark-dialog__member-name">{{ item.name }}</div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="new-lark-dialog__footer">
        <el-button @click="dialogVisible = false"> 取消 </el-button>
        <el-button :loading="submitLoading" type="primary" @click="handleConfirm">
          新建并关联
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import AppMessage from '@/components/message/index.js'
import { addLarkGroup } from '@/api/ai-board'

defineOptions({
  name: 'NewLarkDialog'
})

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: '新建群聊'
  },
  showName: {
    type: String,
    default: ''
  },
  memberList: {
    type: Array,
    default: () => []
  },
  dataId: {
    type: [String, Number],
    default: ''
  },
  businessType: {
    type: [String, Number],
    default: ''
  }
})

const emits = defineEmits(['update:modelValue', 'success'])

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emits('update:modelValue', value)
})

const formData = ref({
  groupName: ''
})
const submitLoading = ref(false)

const getDefaultGroupName = () => props.showName.slice(0, 60)

watch(
  () => props.modelValue,
  (visible) => {
    if (visible) {
      formData.value.groupName = getDefaultGroupName()
    }
  }
)

const getAvatarText = (name = '') => name.slice(-2) || '--'

const displayMemberList = computed(() => {
  return props.memberList.map((item, index) => {
    const name = item.name || ''
    return {
      ...item,
      avatarText: getAvatarText(name)
    }
  })
})

const handleConfirm = async () => {
  if (submitLoading.value) return
  submitLoading.value = true
  try {
    const res = await addLarkGroup({
      dataId: props.dataId,
      businessType: props.businessType,
      groupName: formData.value.groupName,
      userList: props.memberList.map((item) => item.id)
    })

    if (res.result.isSuccess === 1) {
      AppMessage.success('关联成功')
      dialogVisible.value = false
      emits('success')
    } else if (res.result.isSuccess === 0) {
      AppMessage.error('关联失败，请重试')
    }
  } catch (error) {
    // AppMessage.error('关联失败，请重试')
  } finally {
    submitLoading.value = false
  }
}
</script>

<style lang="scss">
.new-lark-dialog {
  margin-top: 15vh;
  border-radius: 12px;
  box-shadow: 0px 2px 15px 2px rgba(0, 0, 0, 0.05);
}

.new-lark-dialog .el-dialog__header {
  border-bottom: none;
}

.new-lark-dialog .el-dialog__body {
  padding: 0;
}

.new-lark-dialog .el-dialog__footer {
  padding: 0;
  height: unset;
  border-top: none;
}
</style>

<style lang="scss" scoped>
.new-lark-dialog__header {
  display: flex;
  align-items: center;
  padding: 24px;
}

.new-lark-dialog__title {
  flex: 1;
  font-family: PingFang SC;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  color: #323233;
}

.new-lark-dialog__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  margin-left: 8px;
  font-size: 16px;
  color: #969799;
  background: transparent;
  border: none;
  cursor: pointer;
}

.new-lark-dialog__body {
  display: flex;
  flex-direction: column;
  padding: 0 24px;
  font-family: PingFang SC;
}

.new-lark-dialog__field {
  display: flex;
  flex-direction: column;
}

.new-lark-dialog__field + .new-lark-dialog__field {
  margin-top: 16px;
}

.new-lark-dialog__label {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #323233;
}

.new-lark-dialog__input-text {
  margin-top: 8px;
}

.new-lark-dialog__input-text :deep(.el-input__wrapper) {
  min-height: 32px;
  background: #ffffff;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  box-shadow: none;
}

.new-lark-dialog__input-text :deep(.el-input__inner) {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #323233;
}

.new-lark-dialog__field--members {
  flex: 1;
}

.new-lark-dialog__member-panel {
  display: flex;
  flex-direction: column;
  min-height: 350px;
  padding: 8px;
  margin-top: 8px;
  overflow-y: auto;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
}

.new-lark-dialog__member-item {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 4px 8px 4px 4px;
  background: #f7f8fa;
  border: 1px solid #f2f3f5;
  border-radius: 64px;
}

.new-lark-dialog__member-item + .new-lark-dialog__member-item {
  margin-top: 8px;
}

.new-lark-dialog__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 8px;
  overflow: hidden;
  font-size: 16px;
  color: #ffffff;
  background: #248df5;
  border-radius: 50%;
}

.new-lark-dialog__avatar--text {
  font-size: 8px;
  font-weight: 600;
  line-height: 1;
  background: #7a52f4;
}

.new-lark-dialog__avatar--robot {
  font-size: 16px;
  line-height: 16px;
  background: #248df5;
}

.new-lark-dialog__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.new-lark-dialog__member-name {
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #323233;
}

.new-lark-dialog__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 20px 24px;
}
</style>
